import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import { createElement } from "@wordpress/element";
/**
 * External dependencies
 */

import classnames from 'classnames';
/**
 * WordPress dependencies
 */

import { useMemo, useEffect, Fragment } from '@wordpress/element';
import { InnerBlocks, InspectorControls, BlockControls, withColors } from '@wordpress/block-editor';
import { createBlock } from '@wordpress/blocks';
import { withSelect, withDispatch } from '@wordpress/data';
import { CheckboxControl, PanelBody, Spinner, Toolbar, Placeholder, Button } from '@wordpress/components';
import { compose } from '@wordpress/compose';
import { __ } from '@wordpress/i18n';
/**
 * Internal dependencies
 */

import useBlockNavigator from './use-block-navigator';
import BlockNavigationList from './block-navigation-list';
import BlockColorsStyleSelector from './block-colors-selector';

function NavigationMenu(_ref) {
  var _classnames;

  var attributes = _ref.attributes,
      clientId = _ref.clientId,
      pages = _ref.pages,
      isRequestingPages = _ref.isRequestingPages,
      hasResolvedPages = _ref.hasResolvedPages,
      backgroundColor = _ref.backgroundColor,
      textColor = _ref.textColor,
      setBackgroundColor = _ref.setBackgroundColor,
      setTextColor = _ref.setTextColor,
      setAttributes = _ref.setAttributes,
      hasExistingNavItems = _ref.hasExistingNavItems,
      updateNavItemBlocks = _ref.updateNavItemBlocks; //
  // HOOKS
  //

  var _useBlockNavigator = useBlockNavigator(clientId),
      navigatorToolbarButton = _useBlockNavigator.navigatorToolbarButton,
      navigatorModal = _useBlockNavigator.navigatorModal;

  useEffect(function () {
    // Set/Unset colors CSS classes.
    setAttributes({
      backgroundColorCSSClass: backgroundColor.class ? backgroundColor.class : null,
      textColorCSSClass: textColor.class ? textColor.class : null
    });
  }, [backgroundColor.class, textColor.class]); // Builds menu items from default Pages

  var defaultPagesMenuItems = useMemo(function () {
    if (!pages) {
      return null;
    }

    return pages.map(function (_ref2) {
      var title = _ref2.title,
          type = _ref2.type,
          url = _ref2.link,
          id = _ref2.id;
      return createBlock('core/navigation-menu-item', {
        type: type,
        id: id,
        url: url,
        label: title.rendered,
        title: title.raw,
        opensInNewTab: false
      });
    });
  }, [pages]); //
  // HANDLERS
  //

  /**
   * Set the color type according to the given values.
   * It propagate the color values into the attributes object.
   * Both `backgroundColorValue` and `textColorValue` are
   * using the inline styles.
   *
   * @param {Object}  colorsData       Arguments passed by BlockColorsStyleSelector onColorChange.
   * @param {string}  colorsData.attr  Color attribute.
   * @param {boolean} colorsData.value Color attribute value.
   */

  var setColorType = function setColorType(_ref3) {
    var attr = _ref3.attr,
        value = _ref3.value;

    switch (attr) {
      case 'backgroundColor':
        setBackgroundColor(value);
        setAttributes({
          backgroundColorValue: value
        });
        break;

      case 'textColor':
        setTextColor(value);
        setAttributes({
          textColorValue: value
        });
        break;
    }
  };

  var handleCreateEmpty = function handleCreateEmpty() {
    var emptyNavItemBlock = createBlock('core/navigation-menu-item');
    updateNavItemBlocks([emptyNavItemBlock]);
  };

  var handleCreateFromExistingPages = function handleCreateFromExistingPages() {
    updateNavItemBlocks(defaultPagesMenuItems);
  };

  var hasPages = hasResolvedPages && pages && pages.length; // If we don't have existing items or the User hasn't
  // indicated they want to automatically add top level Pages
  // then show the Placeholder

  if (!hasExistingNavItems) {
    return createElement(Fragment, null, createElement(InspectorControls, null, hasResolvedPages && createElement(PanelBody, {
      title: __('Menu Settings')
    }, createElement(CheckboxControl, {
      value: attributes.automaticallyAdd,
      onChange: function onChange(automaticallyAdd) {
        setAttributes({
          automaticallyAdd: automaticallyAdd
        });
        handleCreateFromExistingPages();
      },
      label: __('Automatically add new pages'),
      help: __('Automatically add new top level pages to this menu.')
    }))), createElement(Placeholder, {
      className: "wp-block-navigation-menu-placeholder",
      icon: "menu",
      label: __('Navigation Menu'),
      instructions: __('Create a Menu from all existing pages, or create an empty one.')
    }, createElement("div", {
      className: "wp-block-navigation-menu-placeholder__buttons"
    }, createElement(Button, {
      isDefault: true,
      className: "wp-block-navigation-menu-placeholder__button",
      onClick: handleCreateFromExistingPages,
      disabled: !hasPages
    }, __('Create from all top pages')), createElement(Button, {
      isLink: true,
      className: "wp-block-navigation-menu-placeholder__button",
      onClick: handleCreateEmpty
    }, __('Create empty')))));
  } // Build Inline Styles


  var navigationMenuInlineStyles = _objectSpread({}, textColor && {
    color: textColor.color
  }, backgroundColor && {
    backgroundColor: backgroundColor.color
  }); // Build ClassNames


  var navigationMenuClasses = classnames('wp-block-navigation-menu', (_classnames = {
    'has-text-color': textColor.color,
    'has-background-color': backgroundColor.color
  }, _defineProperty(_classnames, attributes.backgroundColorCSSClass, attributes && attributes.backgroundColorCSSClass), _defineProperty(_classnames, attributes.textColorCSSClass, attributes && attributes.textColorCSSClass), _classnames)); // UI State: rendered Block UI

  return createElement(Fragment, null, createElement(BlockControls, null, createElement(Toolbar, null, navigatorToolbarButton), createElement(BlockColorsStyleSelector, {
    backgroundColor: backgroundColor,
    textColor: textColor,
    onColorChange: setColorType
  })), navigatorModal, createElement(InspectorControls, null, hasPages && createElement(PanelBody, {
    title: __('Menu Settings')
  }, createElement(CheckboxControl, {
    value: attributes.automaticallyAdd,
    onChange: function onChange(automaticallyAdd) {
      return setAttributes({
        automaticallyAdd: automaticallyAdd
      });
    },
    label: __('Automatically add new pages'),
    help: __('Automatically add new top level pages to this menu.')
  })), createElement(PanelBody, {
    title: __('Navigation Structure')
  }, createElement(BlockNavigationList, {
    clientId: clientId
  }))), createElement("div", {
    className: navigationMenuClasses,
    style: navigationMenuInlineStyles
  }, !hasExistingNavItems && isRequestingPages && createElement(Fragment, null, createElement(Spinner, null), " ", __('Loading Navigation…'), " "), createElement(InnerBlocks, {
    allowedBlocks: ['core/navigation-menu-item'],
    templateInsertUpdatesSelection: false,
    __experimentalMoverDirection: 'horizontal'
  })));
}

export default compose([withColors({
  backgroundColor: 'background-color',
  textColor: 'color'
}), withSelect(function (select, _ref4) {
  var _select, _select2;

  var clientId = _ref4.clientId;
  var innerBlocks = select('core/block-editor').getBlocks(clientId);
  var hasExistingNavItems = !!innerBlocks.length;
  var filterDefaultPages = {
    parent: 0,
    order: 'asc',
    orderby: 'id'
  };
  var pagesSelect = ['core', 'getEntityRecords', ['postType', 'page', filterDefaultPages]];
  return {
    hasExistingNavItems: hasExistingNavItems,
    pages: select('core').getEntityRecords('postType', 'page', filterDefaultPages),
    isRequestingPages: (_select = select('core/data')).isResolving.apply(_select, pagesSelect),
    hasResolvedPages: (_select2 = select('core/data')).hasFinishedResolution.apply(_select2, pagesSelect)
  };
}), withDispatch(function (dispatch, _ref5) {
  var clientId = _ref5.clientId;
  return {
    updateNavItemBlocks: function updateNavItemBlocks(blocks) {
      dispatch('core/block-editor').replaceInnerBlocks(clientId, blocks);
    }
  };
})])(NavigationMenu);